import { Spinner } from "@blueprintjs/core";
import { useEffect, useRef } from "react";
import { type FallbackProps } from "react-error-boundary";
import { useErrorBoundaryRetryState } from "../context-error-boundary-retry";
import { ErrorBoundaryFailed } from "../ErrorBoundaryFailed/ErrorBoundaryFailed";

export function ErrorBoundaryFallback({ resetErrorBoundary }: FallbackProps) {
  const refAttemptsResetTimeout = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const {
    attemptLimit,
    attempts,
    attemptDelay,
    onNewAttempt,
    onResetAttempts,
  } = useErrorBoundaryRetryState();

  console.log(
    `[ErrorBoundary] Attempt ${attempts} of ${attemptLimit} with delay ${attemptDelay}`,
  );

  useEffect(
    function onComponentMount() {
      onNewAttempt();

      if (refAttemptsResetTimeout.current) {
        clearTimeout(refAttemptsResetTimeout.current);
      }

      return () => {
        refAttemptsResetTimeout.current = setTimeout(() => {
          console.log(`[ErrorBoundary] Reset attempts`);
          refAttemptsResetTimeout.current = undefined;
          onResetAttempts();
        }, attemptDelay * 2);
      };
    },
    [attemptDelay, onNewAttempt, onResetAttempts],
  );

  useEffect(
    function effectOnDelayRetry() {
      let timer: ReturnType<typeof setTimeout> | undefined = undefined;

      if (attempts < attemptLimit) {
        timer = setTimeout(() => {
          console.log(`[ErrorBoundary] Retry attempt ${attempts + 1}`);
          resetErrorBoundary();
        }, attemptDelay);
      }

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    },
    [attemptLimit, attempts, attemptDelay, resetErrorBoundary],
  );

  if (attempts < attemptLimit) {
    return <Spinner />;
  }

  return <ErrorBoundaryFailed />;
}
